import { useCallback, useEffect, useMemo, useState } from 'react';
import { Link, useNavigate, useParams } from 'react-router-dom';
import { Chess } from 'chess.js';
import SiteHeader from '../components/SiteHeader';
import TheoryPanel from '../components/TheoryPanel';
import SideBadge from '../components/SideBadge';
import DifficultyDots from '../components/DifficultyDots';
import PopularityMeter from '../components/PopularityMeter';
import LevelBandPicker from '../components/LevelBandPicker';
import VariationList from '../components/VariationList';
import MistakeExplorer from '../components/MistakeExplorer';
import PlanPanel from '../components/PlanPanel';
import Chessboard from '../board/Chessboard';
import { openingsApi } from '../api/openingsApi';
import { studyApi } from '../api/studyApi';
import { useAuth } from '../auth/AuthContext';
import {
  LINE_LEVELS,
  type Line,
  type LineLevel,
  type Opening,
  type OpeningStats,
  type RepertoireEntry,
  type Side,
} from '../../../shared/types';

/**
 * Everything about one opening: the strategic writeup, and an explorer that
 * steps through each line move by move with the reasoning attached.
 *
 * This is the reading half of the app — no scoring, no scheduling, nothing to
 * get wrong. Practice happens on the study page.
 */
export default function OpeningDetailPage() {
  const { openingId = '' } = useParams();
  const navigate = useNavigate();
  const { user, getToken, refresh } = useAuth();
  const auth = useMemo(() => ({ getToken, refresh }), [getToken, refresh]);

  const [opening, setOpening] = useState<Opening | null>(null);
  const [error, setError] = useState('');
  // Addressed by line id rather than array position. Grouping the list by level
  // reorders what is on screen, and an index would then point at the wrong line
  // -- a bug worth making impossible rather than testing for.
  const [lineId, setLineId] = useState<string | null>(null);
  const [ply, setPly] = useState(0);
  const [inRepertoire, setInRepertoire] = useState<boolean | null>(null);
  // The resolved repertoire entry, or null when the opening is not in it. The
  // server is the authority on what a level band resolves to, so the whole
  // entry is kept rather than recomputing pieces of it here.
  const [entry, setEntry] = useState<RepertoireEntry | null>(null);
  // Levels chosen before the opening has been added, so it enters the
  // repertoire at the levels actually picked rather than always at beginner.
  const [pendingLevels, setPendingLevels] = useState<LineLevel[]>(['beginner']);
  const [busy, setBusy] = useState(false);
  // Collapsed on a phone so the board is visible without scrolling; CSS keeps
  // the list permanently open at desktop widths, where this flag is inert.
  const [listOpen, setListOpen] = useState(false);
  const [flipped, setFlipped] = useState(false);

  useEffect(() => {
    let cancelled = false;
    setOpening(null);
    setLineId(null);
    setPly(0);
    openingsApi
      .get(openingId)
      .then((o) => !cancelled && setOpening(o))
      .catch((e) => !cancelled && setError(e instanceof Error ? e.message : 'Opening not found'));
    return () => {
      cancelled = true;
    };
  }, [openingId]);

  useEffect(() => {
    if (!user) {
      setInRepertoire(null);
      return;
    }
    let cancelled = false;
    studyApi
      .repertoire(auth)
      .then((entries: RepertoireEntry[]) => {
        if (cancelled) return;
        const found = entries.find((e) => e.openingId === openingId) ?? null;
        setInRepertoire(!!found);
        setEntry(found);
      })
      .catch(() => !cancelled && setInRepertoire(false));
    return () => {
      cancelled = true;
    };
  }, [user, auth, openingId]);

  // Falls back to the first line, which is also the initial state and what a
  // stale id from a previous opening should resolve to.
  // Lines *at* each level, not cumulative: a band is a set, so "advanced" means
  // the six advanced lines, not all fifteen up to and including them.
  const levelCounts = useMemo(() => {
    const out: Partial<Record<LineLevel, number>> = {};
    if (!opening) return out;
    LINE_LEVELS.forEach((lv) => {
      const n = opening.lines.filter((l) => l.level === lv).length;
      if (n > 0) out[lv] = n;
    });
    return out;
  }, [opening]);

  // Before the opening is added there is no server entry, so the picker is
  // driven by the local pending choice instead.
  const pendingByLevel = useMemo(() => {
    const out: Partial<Record<LineLevel, number>> = {};
    pendingLevels.forEach((lv) => {
      if (levelCounts[lv]) out[lv] = levelCounts[lv];
    });
    return out;
  }, [pendingLevels, levelCounts]);

  const selected = entry?.selectedLineIds ?? null;

  const line: Line | null =
    opening?.lines.find((l) => l.id === lineId) ?? opening?.lines[0] ?? null;

  // The position after `ply` moves of the selected line.
  const { fen, lastMove } = useMemo(() => {
    const chess = new Chess();
    let last: { from: string; to: string } | null = null;
    if (line) {
      for (const move of line.moves.slice(0, ply)) {
        const played = chess.move(move.san);
        last = { from: played.from, to: played.to };
      }
    }
    return { fen: chess.fen(), lastMove: last };
  }, [line, ply]);

  const step = useCallback(
    (delta: number) => {
      if (!line) return;
      setPly((p) => Math.max(0, Math.min(line.moves.length, p + delta)));
    },
    [line],
  );

  // Arrow keys step through the line — the natural way to read one.
  useEffect(() => {
    function onKey(e: KeyboardEvent) {
      const target = e.target as HTMLElement | null;
      if (target && ['INPUT', 'TEXTAREA', 'SELECT'].includes(target.tagName)) return;
      if (e.key === 'ArrowRight') { e.preventDefault(); step(1); }
      if (e.key === 'ArrowLeft') { e.preventDefault(); step(-1); }
    }
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, [step]);

  /** Choose which level bands to study. Works before the opening is added. */
  async function changeLevels(levels: LineLevel[]) {
    if (!inRepertoire) {
      setPendingLevels(levels); // applied when the opening is added
      return;
    }
    const previous = entry;
    setBusy(true);
    setError('');
    try {
      setEntry(await studyApi.setRepertoireSelection(openingId, { levels }, auth));
    } catch (e) {
      setEntry(previous);
      setError(e instanceof Error ? e.message : 'Could not change your levels');
    } finally {
      setBusy(false);
    }
  }

  /** Tick or untick one variation. The server turns the set into overrides. */
  async function toggleLine(id: string) {
    if (!opening || !inRepertoire || !selected) return;
    const next = selected.includes(id)
      ? selected.filter((x) => x !== id)
      : [...selected, id];
    if (next.length === 0) {
      setError('Keep at least one variation, or remove the opening entirely.');
      return;
    }
    const previous = entry;
    try {
      setEntry(await studyApi.setRepertoireSelection(openingId, { lines: next }, auth));
    } catch (e) {
      setEntry(previous);
      setError(e instanceof Error ? e.message : 'Could not change your selection');
    }
  }

  /** Add the opening (if needed) with just this variation, then practice it. */
  async function practiceOnly(id: string, mode: 'study' | 'practice') {
    if (!user) {
      navigate('/login', { state: { from: `/openings/${openingId}` } });
      return;
    }
    setBusy(true);
    try {
      if (!inRepertoire) {
        await studyApi.addToRepertoire(openingId, auth);
        setInRepertoire(true);
      }
      setEntry(await studyApi.setRepertoireSelection(openingId, { lines: [id] }, auth));
      navigate(`/study?opening=${openingId}&line=${encodeURIComponent(id)}&mode=${mode}`);
    } catch (e) {
      setError(e instanceof Error ? e.message : 'Could not start that variation');
    } finally {
      setBusy(false);
    }
  }

  async function toggleRepertoire() {
    if (!user) {
      navigate('/login', { state: { from: `/openings/${openingId}` } });
      return;
    }
    setBusy(true);
    try {
      if (inRepertoire) {
        await studyApi.removeFromRepertoire(openingId, auth);
        setInRepertoire(false);
        setEntry(null);
      } else {
        await studyApi.addToRepertoire(openingId, auth);
        // Apply the levels chosen before adding, so the opening enters at the
        // bands actually picked rather than silently at beginner.
        setEntry(
          await studyApi.setRepertoireSelection(openingId, { levels: pendingLevels }, auth),
        );
        setInRepertoire(true);
      }
    } catch (e) {
      setError(e instanceof Error ? e.message : 'Could not update your repertoire');
    } finally {
      setBusy(false);
    }
  }

  if (error && !opening) {
    return (
      <>
        <SiteHeader />
        <main className="page">
          <p className="error">{error}</p>
          <Link className="btn" to="/openings">Back to openings</Link>
        </main>
      </>
    );
  }

  if (!opening || !line) {
    return (
      <>
        <SiteHeader />
        <main className="page"><p className="muted">Loading…</p></main>
      </>
    );
  }

  // The move whose explanation is showing: the one just played.
  const currentMove = ply > 0 ? line.moves[ply - 1] : null;
  const orientation = flipped
    ? opening.side === 'white' ? 'black' : 'white'
    : (opening.side as 'white' | 'black');

  return (
    <>
      <SiteHeader />
      <main className="page page--wide">
        <header className="detail-head">
          <div>
            <Link to="/openings" className="back-link">← All openings</Link>
            <h1 className="detail-title">{opening.name}</h1>
            <div className="detail-tags">
              <SideBadge side={opening.side} />
              <span className="tag">{opening.eco}</span>
              <span className="tag">{opening.family}</span>
              <DifficultyDots level={opening.difficulty} />
              <PopularityMeter value={opening.popularity} stats={opening.stats} />
            </div>
            <code className="opening-moves opening-moves--large">{opening.moves}</code>
            {opening.stats && <OpeningStatsLine stats={opening.stats} side={opening.side} />}
            <p className="detail-summary">{opening.summary}</p>
          </div>
          <div className="detail-actions">
            <button
              type="button"
              className={`btn ${inRepertoire ? '' : 'btn--primary'}`}
              onClick={toggleRepertoire}
              disabled={busy}
            >
              {inRepertoire ? 'In your repertoire ✓' : 'Add to repertoire'}
            </button>
            {/* Bands rather than a cumulative level: "intermediate and above,
                but not beginner" is a reasonable thing to want and a floor
                cannot express it. Shown before adding too, so an opening enters
                the repertoire at the levels you actually chose. */}
            <LevelBandPicker
              counts={levelCounts}
              selectedByLevel={entry?.selectedByLevel ?? pendingByLevel}
              onChange={changeLevels}
              disabled={busy}
            />
            {inRepertoire && (
              <div className="rep-actions">
                <Link className="btn btn--primary" to={`/study?opening=${opening.id}&mode=study`}>
                  Study all {selected?.length ?? ''}
                </Link>
                <Link className="btn" to={`/study?opening=${opening.id}&mode=practice`}>
                  Practice all
                </Link>
              </div>
            )}
            {!user && <p className="muted detail-signin-note">Sign in to track your progress.</p>}
          </div>
        </header>

        {error && <p className="error">{error}</p>}

        <div className="detail-layout">
          <VariationList
            lines={opening.lines}
            activeId={line?.id ?? null}
            onSelect={(id) => {
              setLineId(id);
              setPly(0);
              setListOpen(false); // on a phone, choosing should reveal the board
            }}
            selectedIds={inRepertoire ? selected : null}
            onToggleSelected={toggleLine}
            open={listOpen}
            onToggle={() => setListOpen((o) => !o)}
          />

          <div className="detail-board">
            <Chessboard
              fen={fen}
              orientation={orientation}
              disabled
              lastMove={lastMove}
              maxSize={460}
            />

            {line && (
              <div className="line-actions">
                <span className="line-actions-label">
                  <strong>{line.name}</strong>
                  <span className="muted"> · {Math.ceil(line.moves.length / 2)} of your moves</span>
                </span>
                <button
                  type="button"
                  className="btn btn--small"
                  disabled={busy}
                  onClick={() => practiceOnly(line.id, 'study')}
                >
                  Study this line
                </button>
                <button
                  type="button"
                  className="btn btn--small btn--primary"
                  disabled={busy}
                  onClick={() => practiceOnly(line.id, 'practice')}
                >
                  Practice this line
                </button>
              </div>
            )}

            <div className="board-controls">
              <button type="button" className="btn btn--small" onClick={() => setPly(0)} disabled={ply === 0}>
                ⏮
              </button>
              <button type="button" className="btn btn--small" onClick={() => step(-1)} disabled={ply === 0}>
                ← Back
              </button>
              <span className="ply-count">
                {ply} / {line.moves.length}
              </span>
              <button
                type="button"
                className="btn btn--small btn--primary"
                onClick={() => step(1)}
                disabled={ply >= line.moves.length}
              >
                Next →
              </button>
              <button type="button" className="btn btn--small" onClick={() => setFlipped((f) => !f)}>
                Flip
              </button>
            </div>

            <div className="explain-card">
              {currentMove ? (
                <>
                  <h3 className="explain-title">
                    {moveNumberLabel(ply)} {currentMove.san}
                    {currentMove.critical && <span className="coach-key">key move</span>}
                  </h3>
                  {currentMove.why ? (
                    <p className="coach-why">{currentMove.why}</p>
                  ) : (
                    <p className="muted">
                      The opponent's reply. Step forward to see what you should play against it.
                    </p>
                  )}
                  {currentMove.mistakes?.map((m) => (
                    <MistakeExplorer
                      key={m.san}
                      prefix={line.moves.slice(0, ply - 1).map((mv) => mv.san)}
                      mistake={m}
                      orientation={orientation}
                    />
                  ))}
                </>
              ) : (
                <>
                  <h3 className="explain-title">{line.name}</h3>
                  <p className="coach-why">{line.summary}</p>
                  <p className="muted">
                    Step through with the Next button or the arrow keys. Every move you would have to
                    find comes with the reason it is played.
                  </p>
                </>
              )}

              {ply === line.moves.length && (
                <>
                  <div className="explain-final">
                    <h4>Where this leaves you</h4>
                    <p>{line.finalComment}</p>
                  </div>
                  {/* The moves are learned by here; this is the part that was
                      missing, and the reason a line used to just stop. */}
                  {line.plan && <PlanPanel plan={line.plan} side={line.side} />}
                </>
              )}
            </div>
          </div>

          <aside className="detail-theory">
            <h2 className="theory-heading">The theory</h2>
            <TheoryPanel
              theory={opening.theory}
              position={{ fen: opening.fen, side: opening.side, moves: opening.moves }}
            />
          </aside>
        </div>
      </main>
    </>
  );
}

/**
 * The numbers behind the popularity meter, stated plainly.
 *
 * The score is given from the trainee's own side: "White scores 52%" is a fact
 * you have to mentally invert every single time you are studying a defense, and
 * this page already knows which color you would be playing.
 */
function OpeningStatsLine({ stats, side }: { stats: OpeningStats; side: Side }) {
  const score = side === 'white' ? stats.whiteScore : Math.round((100 - stats.whiteScore) * 10) / 10;
  return (
    <p className="stats-line muted">
      <strong>{stats.games.toLocaleString()}</strong> club games ({stats.ratings})
      <span className="stats-sep">·</span>
      <strong>{stats.sharePercent}%</strong> of games from the previous move
      <span className="stats-sep">·</span>
      scores <strong>{score}%</strong> for {side}
    </p>
  );
}

/** "3." for White's third move, "3..." for Black's. */
function moveNumberLabel(ply: number): string {
  const moveNumber = Math.ceil(ply / 2);
  return ply % 2 === 1 ? `${moveNumber}.` : `${moveNumber}...`;
}
